/* eslint-disable react-refresh/only-export-components */
import { createFileRoute } from "@tanstack/react-router";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import { Download } from "lucide-react";
import { useState } from "react";
import { mediaItems } from "@/data";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/_public/media")({
  component: MediaPage,
});

const categories = ["All", "Events", "Training", "Posters"];
function MediaPage() {
  const [activeCategory, setActiveCategory] = useState("All");

  const filtered = mediaItems.filter(
    (m) => activeCategory === "All" || m.category === activeCategory,
  );

  return (
    <>
      <Helmet>
        <title>Media Centre | Rosewood Tax Consultancy Kenya</title>
        <meta
          name="description"
          content="Browse Rosewood Tax Consultancy's media library — event photos, training videos, and informational posters."
        />
      </Helmet>

      <section className="bg-cream pt-16 pb-14">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <h1 className=" text-5xl md:text-6xl font-semibold text-[#1A1A1A] mb-4">
              Events & Media
            </h1>
            <p className="text-lg text-neutral-800 max-w-xl">
              Stay updated with our latest events, training sessions, and
              informational resources.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="py-10 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-wrap gap-2">
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className={cn(
                  "px-5 py-2 rounded-full text-sm font-medium transition-all",
                  activeCategory === cat
                    ? "bg-burgundy text-white"
                    : "bg-cream text-neutral-800",
                )}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="py-14 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          {filtered.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-neutral-800">
                No media found in this category.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filtered.map((item, i) => (
                <motion.div
                  key={item.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.08 }}
                  className="group bg-cream rounded-xl overflow-hidden hover:-translate-y-0.5 transition-all cursor-pointer"
                >
                  <div className="aspect-video bg-burgundy/8 overflow-hidden relative flex items-center justify-center">
                    {item.type === "video" ? (
                      <>
                        {item.thumbnail ? (
                          <img
                            src={item.thumbnail}
                            alt={item.title}
                            className="w-full h-full object-cover"
                            loading="lazy"
                          />
                        ) : (
                          <div className="absolute inset-0 flex items-center justify-center">
                            <div className="w-16 h-16 rounded-full bg-burgundy/15 flex items-center justify-center">
                              <span className="text-2xl font-bold text-burgundy/40">
                                {item.category.charAt(0)}
                              </span>
                            </div>
                          </div>
                        )}
                        <div className="absolute inset-0 flex items-center justify-center">
                          <div className="w-12 h-12 rounded-full bg-white/90 flex items-center justify-center shadow-md group-hover:scale-110 transition-transform">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="20"
                              height="20"
                              viewBox="0 0 24 24"
                              fill="#6B1E2A"
                              stroke="#6B1E2A"
                              strokeWidth="2"
                            >
                              <polygon points="5 3 19 12 5 21 5 3" />
                            </svg>
                          </div>
                        </div>
                      </>
                    ) : item.url ? (
                      <img
                        src={item.url}
                        alt={item.title}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                    ) : (
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="w-16 h-16 rounded-full bg-burgundy/15 flex items-center justify-center">
                          <span className="text-2xl font-bold text-burgundy/40">
                            {item.category.charAt(0)}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="p-5">
                    <span className="text-xs font-medium text-[#C9A84C] uppercase tracking-wide">
                      {item.category}
                    </span>
                    <h3 className="font-semibold text-[#1A1A1A] mt-1 mb-2">
                      {item.title}
                    </h3>
                    <p className="text-sm text-neutral-800 line-clamp-2 mb-4">
                      {item.description}
                    </p>
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-neutral-800">
                        {new Date(item.uploadedAt).toLocaleDateString("en-US", {
                          year: "numeric",
                          month: "short",
                          day: "numeric",
                        })}
                      </span>
                      {item.type === "poster" && (
                        <button className="flex items-center gap-1.5 text-xs font-semibold text-burgundy hover:text-[#4A1219] transition-colors">
                          <Download size={13} />
                          Download
                        </button>
                      )}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>
    </>
  );
}
